import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { purchasingService } from "../services/dataService";
import { PurchaseOrderDetail, PurchaseOrderListItem, PurchaseOrderStatus as S } from "../types";
import { formatCurrency } from "../utils/formatters";
import { usePermissions } from "../context/PermissionContext";
import { PO_STATUS_LABEL, poDue, poErr, poFmtDate, poFmtQty, poLayerBlocked } from "../utils/purchasing";
import { firmHue, firmInitials } from "./FirmPicker";
import "../pages/PurchaseOrders.css";

/**
 * Satın alma siparişi kartı (çekmece) — salt okunur belge görünümü. İşlemleri kendisi yapmaz;
 * sayfadan ister (düzenle / ilet / mal kabul / iptal / sil), böylece onay ve bildirimler tek yerde durur.
 */
export interface PurchaseOrderDrawerProps {
  orderId: string;
  /** Liste satırı: ayrıntı yüklenirken başlık anında dolar. */
  preview?: PurchaseOrderListItem;
  /** Sayfa her değişiklikten sonra artırır → ayrıntı yeniden çekilir. */
  refreshKey: number;
  busy?: boolean;
  escDisabled?: boolean;
  onClose: () => void;
  onEdit: (o: PurchaseOrderDetail) => void;
  onSend: (o: PurchaseOrderListItem) => void;
  onReceive: (o: PurchaseOrderDetail) => void;
  onCancel: (o: PurchaseOrderListItem) => void;
  onDelete: (o: PurchaseOrderListItem) => void;
}

const I = {
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>,
  edit: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>,
  trash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" /></svg>,
  send: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2 11 13M22 2l-7 20-4-9-9-4z" /></svg>,
  receive: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v12M7 10l5 5 5-5M4 19h16" /></svg>,
  inbox: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16v16H4z" /><path d="M4 13h5l2 3h2l2-3h5" /></svg>,
};

const PurchaseOrderDrawer: React.FC<PurchaseOrderDrawerProps> = ({ orderId, preview, refreshKey, busy, escDisabled, onClose, onEdit, onSend, onReceive, onCancel, onDelete }) => {
  const navigate = useNavigate();
  const { has, hasModule } = usePermissions();
  const [order, setOrder] = useState<PurchaseOrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const overlayRef = useRef<HTMLDivElement>(null);
  const asideRef = useRef<HTMLElement>(null);
  const gen = useRef(0);

  const load = useCallback(async () => {
    const g = ++gen.current;
    setLoading(true); setError("");
    try {
      const d = await purchasingService.order(orderId);
      if (g === gen.current) setOrder(d);
    } catch (e: any) {
      if (g === gen.current) { setOrder(null); setError(e?.response?.status === 404 ? "" : poErr(e, "")); }
    } finally { if (g === gen.current) setLoading(false); }
  }, [orderId]);
  // Başka bir siparişe geçilince eski kart gösterilmesin; aynı siparişte yenilemede eski içerik kalır
  useEffect(() => { setOrder((o) => (o && o.purchaseOrderId === orderId ? o : null)); }, [orderId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  // aria-modal kart odağı alır (derin bağlantıda da); kapanınca odak açıldığı yere döner
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    return () => { if (prev && prev.isConnected) prev.focus?.({ preventScroll: true }); };
  }, []);
  useEffect(() => {
    const el = asideRef.current;
    if (el && !el.contains(document.activeElement)) el.focus({ preventScroll: true });
  }, [orderId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !e.defaultPrevented && !escDisabled && !poLayerBlocked(overlayRef.current)) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, escDisabled]);

  const h: PurchaseOrderListItem | null = order ?? preview ?? null;
  const due = h ? poDue(h.expectedDate, h.status) : { state: "" as const, days: null, text: "" };
  const canUpdate = has("purchasing:orders:update");
  const canDelete = has("purchasing:orders:delete");
  const pct = h && h.orderedQuantity > 0 ? Math.min(100, Math.round((h.receivedQuantity / h.orderedQuantity) * 100)) : 0;
  const sup = order?.supplier;
  const money = (v: number) => formatCurrency(v, h?.currency || "TRY");

  return (
    <div className="po-overlay" ref={overlayRef} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <aside className="po-drawer" ref={asideRef} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="po-dr-title">
        <div className="po-drawer-head">
          <div className="po-drawer-title">
            {h && <span className="po-mark big" style={{ ["--po-hue" as any]: firmHue(h.supplierName) }}>{firmInitials(h.supplierName)}</span>}
            <div>
              <span className="po-kicker">Satın alma siparişi{h ? ` · ${poFmtDate(h.orderDate)}` : ""}</span>
              <h2 id="po-dr-title">{h?.number ?? "Sipariş"}</h2>
              {h && <div className="po-drawer-sup">{h.supplierName}</div>}
              {h && (
                <div className="po-badges">
                  <span className={`po-st s${h.status}`}>{PO_STATUS_LABEL[h.status]}</span>
                  {due.state && <span className={`po-badge ${due.state === "late" ? "err" : "warn"}`}>{due.text}</span>}
                  <span className="po-badge muted">{h.currency}</span>
                  {!hasModule("stock") && <span className="po-badge muted">stok hareketi üretilmez</span>}
                </div>
              )}
            </div>
          </div>
          <button type="button" className="po-ib big" aria-label="Kapat" onClick={onClose}>{I.close}</button>
        </div>

        <div className="po-drawer-body">
          {!order && loading && <div className="po-loading"><span className="po-spin" /> Sipariş yükleniyor</div>}

          {!order && !loading && (
            <div className="po-blank">
              <span className="po-blank-icon">{I.inbox}</span>
              <b>Sipariş bulunamadı</b>
              {error || "Silinmiş ya da erişiminiz yok."}
              <div className="po-actrow" style={{ justifyContent: "center" }}>
                <button type="button" className="po-btn" onClick={load}>Yeniden dene</button>
                <button type="button" className="po-btn primary" onClick={onClose}>Listeye dön</button>
              </div>
            </div>
          )}

          {order && (
            <>
              {/* İşlemler */}
              {order.status === S.Draft && (canUpdate || canDelete) && (
                <div className="po-actrow">
                  {canUpdate && <button type="button" className="po-btn small primary" disabled={busy} onClick={() => onSend(order)}>{I.send} Tedarikçiye ilet</button>}
                  {canUpdate && <button type="button" className="po-btn small" disabled={busy} onClick={() => onEdit(order)}>{I.edit} Düzenle</button>}
                  {canUpdate && <button type="button" className="po-btn small danger push" disabled={busy} onClick={() => onCancel(order)}>İptal et</button>}
                  {canDelete && <button type="button" className={`po-btn small danger ${canUpdate ? "" : "push"}`} disabled={busy} onClick={() => onDelete(order)}>{I.trash} Sil</button>}
                </div>
              )}
              {order.status === S.Ordered && canUpdate && (
                <div className="po-actrow">
                  <button type="button" className="po-btn small primary" disabled={busy} onClick={() => onReceive(order)}>{I.receive} Mal kabul</button>
                  <button type="button" className="po-btn small danger push" disabled={busy} onClick={() => onCancel(order)}>İptal et</button>
                </div>
              )}
              {order.status === S.PartiallyReceived && canUpdate && (
                <div className="po-actrow">
                  <button type="button" className="po-btn small primary" disabled={busy} onClick={() => onReceive(order)}>{I.receive} Mal kabul</button>
                  <span className="po-hint push">Kısmen teslim alınan sipariş iptal edilemez.</span>
                </div>
              )}
              {order.status === S.Cancelled && (
                <div className="po-note"><b>İptal edildi{order.cancelledAt ? ` · ${poFmtDate(order.cancelledAt)}` : ""}</b>{order.cancelReason ? ` — ${order.cancelReason}` : ""}</div>
              )}

              {/* Rakamlar */}
              <div className="po-figs">
                <div className="po-fig"><span className="l">Tutar · KDV dahil</span><span className="v">{money(order.grossTotal)}</span><span className="s">net {money(order.netTotal)}</span></div>
                <div className={`po-fig ${due.state}`}><span className="l">Beklenen teslim</span><span className="v">{poFmtDate(order.expectedDate)}</span><span className="s">{due.text || (order.status === S.Draft ? "henüz iletilmedi" : order.status === S.Received ? "teslim alındı" : " ")}</span></div>
                <div className="po-fig"><span className="l">Teslimat</span><span className="v">{poFmtQty(order.receivedQuantity)} / {poFmtQty(order.orderedQuantity)}</span>
                  <div className={`po-progress wide ${pct >= 100 ? "done" : due.state === "late" ? "late" : ""}`}><div className="po-progress-bar"><i style={{ width: `${pct}%` }} /></div></div>
                  <span className="s">%{pct} teslim alındı</span></div>
              </div>

              {/* Künye */}
              <dl className="po-facts">
                <div className="wide"><dt>Tedarikçi</dt>
                  <dd>{order.supplierName}{sup && sup.isActive === false && <span className="po-badge muted" style={{ marginLeft: "0.4rem" }}>Pasif</span>}
                    {sup && (sup.taxNumber || sup.taxOffice) && <small>{[sup.taxNumber && `VKN ${sup.taxNumber}`, sup.taxOffice].filter(Boolean).join(" · ")}</small>}
                    {sup && <small>{sup.contactPerson || sup.phone || sup.email
                      ? <>{sup.contactPerson}{sup.contactPerson && (sup.phone || sup.email) ? " · " : ""}{sup.phone && <a href={`tel:${sup.phone}`}>{sup.phone}</a>}{sup.phone && sup.email ? " · " : ""}{sup.email && <a href={`mailto:${sup.email}`}>{sup.email}</a>}</>
                      : "İletişim bilgisi yok"}</small>}
                    {sup && <small>Vade {sup.paymentTermDays} gün</small>}
                  </dd></div>
                <div><dt>Sipariş tarihi</dt><dd>{poFmtDate(order.orderDate)}</dd></div>
                <div><dt>Oluşturan</dt><dd>{order.createdBy || "—"}</dd></div>
                {order.orderedAt && <div><dt>İletildi</dt><dd>{poFmtDate(order.orderedAt)}</dd></div>}
                <div className="wide"><dt>Not</dt><dd>{order.note || "—"}</dd></div>
              </dl>

              {/* Kalemler */}
              <section className="po-sec">
                <h3>Kalemler <em>{order.lines.length}</em></h3>
                <div className="po-table-wrap tight">
                  <table className="po-table small">
                    <thead><tr><th>#</th><th>Stok</th><th className="num">Miktar</th><th className="num">Birim fiyat</th><th>KDV</th>{order.status !== S.Draft && <th className="num">Alınan</th>}<th className="num">Tutar</th></tr></thead>
                    <tbody>
                      {order.lines.map((l, i) => (
                        <tr key={l.purchaseOrderLineId} className="static">
                          <td>{i + 1}</td>
                          <td><span className="po-name">{l.stockName}</span>{l.stockCode && <span className="po-codepill">{l.stockCode}</span>}{l.description && <div className="po-sub">{l.description}</div>}</td>
                          <td className="num">{poFmtQty(l.quantity)} {l.unit}</td>
                          <td className="num">{money(l.unitPrice)}</td>
                          <td>%{l.vatRate}</td>
                          {order.status !== S.Draft && (
                            <td className="num">{poFmtQty(l.receivedQuantity)}
                              {l.remaining > 0.0005 ? <div className="po-sub">kalan {poFmtQty(l.remaining)}</div> : <div><span className="po-badge ok">Tam</span></div>}
                            </td>
                          )}
                          <td className="num"><span className="po-amt">{money(l.net)}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="po-totals">
                  <div className="po-total-row"><span>Ara toplam</span><b>{money(order.netTotal)}</b></div>
                  <div className="po-total-row"><span>KDV</span><b>{money(order.vatTotal)}</b></div>
                  <div className="po-total-row grand"><span>Genel toplam</span><b>{money(order.grossTotal)}</b></div>
                </div>
              </section>

              {/* Geçmiş */}
              <section className="po-sec">
                <h3>Geçmiş <em>{order.receipts.length} teslimat</em></h3>
                <div className="po-timeline">
                  <div className="po-tl"><b>Oluşturuldu</b><small>{poFmtDate(order.orderDate)} · {order.createdBy || "—"}</small></div>
                  {order.orderedAt && <div className="po-tl"><b>Tedarikçiye iletildi</b><small>{poFmtDate(order.orderedAt)}</small></div>}
                  {[...order.receipts].sort((a, b) => (a.receiptDate < b.receiptDate ? -1 : 1)).map((r) => (
                    <div className="po-tl" key={r.purchaseReceiptId}>
                      <b>Mal kabul · İrsaliye {r.waybillNo || "yok"}</b>
                      <small>{poFmtDate(r.receiptDate)} · {r.receivedBy || "—"}{r.note ? ` — ${r.note}` : ""}</small>
                      <ul className="po-tl-lines">
                        {r.lines.map((l, k) => <li key={k}>{l.stockName} — {poFmtQty(l.quantity)} {l.unit}{!l.stockMovementCreated && <span className="po-badge muted">stok hareketi yok</span>}</li>)}
                      </ul>
                    </div>
                  ))}
                  {order.status === S.Cancelled && <div className="po-tl"><b>İptal edildi</b><small>{poFmtDate(order.cancelledAt)}{order.cancelReason ? ` — ${order.cancelReason}` : ""}</small></div>}
                </div>
                {order.status === S.Ordered && order.receipts.length === 0 && <p className="po-hint">Henüz mal kabul yapılmadı.</p>}
              </section>

              {hasModule("finance") && has("payables:view") && (
                <div className="po-actrow">
                  <button type="button" className="po-link" onClick={() => navigate(`/payables?supplier=${order.supplierId}`)}>Tedarikçi carisi</button>
                </div>
              )}
            </>
          )}
        </div>
      </aside>
    </div>
  );
};

export default PurchaseOrderDrawer;
